/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import * as RDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import type { JSX, ReactNode } from 'react';

interface ModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description?: string | undefined;
  readonly closeLabel: string;
  readonly children: ReactNode;
}

/** Accessible modal dialog (Radix) — used for the portal "Add …" forms. */
export function Modal({
  open,
  onOpenChange,
  title,
  description,
  closeLabel,
  children,
}: ModalProps): JSX.Element {
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm" />
        <RDialog.Content className="fixed left-1/2 top-1/2 max-h-[90vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl bg-white p-6 shadow-xl focus:outline-none">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <RDialog.Title className="text-lg font-semibold text-slate-800">
                {title}
              </RDialog.Title>
              {description !== undefined && (
                <RDialog.Description className="mt-1 text-sm text-slate-500">
                  {description}
                </RDialog.Description>
              )}
            </div>
            <RDialog.Close
              className="focus-ring rounded-lg p-1 text-slate-400 hover:bg-slate-100"
              aria-label={closeLabel}
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </RDialog.Close>
          </div>
          {children}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}
